import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EmailStatus } from '../api';
import { CheckIcon, CodeIcon, CopyIcon, DotsIcon } from './icons';

/* ---------- Dates (always shown in IST) ---------- */

const IST = 'Asia/Kolkata';

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** `28 Oct 2026`: a calendar date in IST, for billing periods and invoices. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${new Date(iso).toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })} IST`;
}

export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const seconds = Math.round((now - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months} mo ago` : `${Math.round(months / 12)} y ago`;
}

/* ---------- Status badges ---------- */

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'accent' | '';

const EMAIL_TONES: Record<EmailStatus, Tone> = {
  queued: '',
  scheduled: 'info',
  sending: 'accent',
  sent: 'accent',
  delivered: 'ok',
  delivery_delayed: 'warn',
  bounced: 'bad',
  complained: 'warn',
  suppressed: '',
  failed: 'bad',
  canceled: '',
};

const LABELS: Record<string, string> = {
  delivery_delayed: 'Delayed',
  temporary_failure: 'Retrying',
  rendering_failure: 'Rendering failed',
};

export const label = (status: string) =>
  LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1).replaceAll('_', ' ');

export function toneOf(status: string): Tone {
  return (
    EMAIL_TONES[status as EmailStatus] ??
    (status === 'verified' || status === 'enabled' || status === 'succeeded'
      ? 'ok'
      : status === 'failed' || status === 'disabled'
        ? 'bad'
        : status === 'pending' || status === 'temporary_failure'
          ? 'warn'
          : '')
  );
}

export function Status({ status, tone }: { status: string; tone?: Tone }) {
  return <span className={`status ${tone ?? toneOf(status)}`}>{label(status)}</span>;
}

/* ---------- Copy ---------- */

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1500);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <button
      type="button"
      className="icon-btn"
      title={done ? 'Copied' : label}
      aria-label={done ? 'Copied' : label}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
        } catch {
          // Clipboard blocked: the value is still selectable.
        }
      }}
    >
      {done ? <CheckIcon className="copied" style={{ color: 'var(--ok)' }} /> : <CopyIcon />}
    </button>
  );
}

export function Copyable({ value, display }: { value: string; display?: string }) {
  return (
    <span className="copyable">
      <code title={value}>{display ?? value}</code>
      <CopyButton value={value} />
    </span>
  );
}

/* ---------- Layout bits ---------- */

/**
 * The header at the top of every page: title, optional one-line description, actions on the
 * right. It always takes the same height (the description line is reserved even when empty), so
 * the title, the buttons and the first card sit at the same place on every page.
 */
export function PageHeader({
  title,
  description,
  actions,
  crumbs,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  crumbs?: ReactNode;
}) {
  return (
    <>
      {crumbs ? <div className="crumbs">{crumbs}</div> : null}
      <header className="page-head">
        <h1>{title}</h1>
        {actions ? <div className="actions">{actions}</div> : null}
        <p>{description}</p>
      </header>
    </>
  );
}

export function Empty({
  icon,
  title,
  children,
  actions,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="tile">{icon}</span>
      <h3>{title}</h3>
      {children}
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function CodeBlock({ code }: { code: string }) {
  return (
    <div className="code-block">
      <CopyButton value={code} />
      <pre>{code}</pre>
    </div>
  );
}

export function SkeletonRows({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows never reorder
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: placeholder cells never reorder
            <td key={c}>
              <div className="skeleton" style={{ width: `${50 + ((r * 7 + c * 13) % 40)}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="form-error">{error instanceof Error ? error.message : String(error)}</p>;
}

/* ---------- Menus ---------- */

/** Closes when clicking outside `ref` or pressing Escape. */
export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return ref;
}

/**
 * A "⋯" button with a small menu of actions. The menu is rendered on <body> with fixed position,
 * so a table's scroll container can't clip it, and it opens upwards when there's no room below.
 */
export function RowMenu({
  items,
  label = 'Actions',
}: {
  items: { label: string; onSelect: () => void; danger?: boolean }[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top: number; right: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const b = button.current?.getBoundingClientRect();
    const height = menu.current?.offsetHeight ?? 0;
    if (!b) return;
    const below = b.bottom + 4;
    const fitsBelow = below + height <= window.innerHeight - 8;
    setPlace({
      top: fitsBelow || b.top - 4 - height < 8 ? below : b.top - 4 - height,
      right: document.documentElement.clientWidth - b.right,
    });
    // A fixed menu would drift away from its button: close it instead.
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!button.current?.contains(target) && !menu.current?.contains(target)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      button.current?.focus();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: stops row navigation when using the menu
    // biome-ignore lint/a11y/useKeyWithClickEvents: the inner button handles keyboard use
    <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
      <button
        ref={button}
        type="button"
        className="icon-btn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <DotsIcon />
      </button>
      {open
        ? createPortal(
            // Events from the portal still bubble to the wrapper above, which stops row clicks.
            <div
              ref={menu}
              className="menu floating"
              role="menu"
              style={
                place ? { top: place.top, right: place.right } : { top: 0, visibility: 'hidden' }
              }
            >
              {items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  className={item.danger ? 'danger' : undefined}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/* ---------- Dialog ---------- */

export function Dialog({
  title,
  description,
  children,
  footer,
  onClose,
  wide,
}: {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  // Focus the first field once when the dialog opens; Escape closes it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: clicking the backdrop closes; Escape works too
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled on the document
    <div className="dialog-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`dialog${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
      >
        <div className="dialog-head">
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        {children ? <div className="dialog-body">{children}</div> : null}
        <div className="dialog-foot">{footer}</div>
      </div>
    </div>
  );
}

/** The `</>` button: shows how to do what this page does with the API. */
export function CodeButton({
  title,
  snippets,
}: {
  title: string;
  snippets: { label: string; code: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        aria-label="Show API code"
        title="Show API code"
        onClick={() => setOpen(true)}
      >
        <CodeIcon />
      </button>
      {open ? (
        <Dialog
          wide
          title={title}
          description="Everything in the dashboard can be done with the API."
          onClose={() => setOpen(false)}
          footer={
            <>
              <a
                className="btn btn-ghost"
                href="https://vaartix.com/docs/api/"
                target="_blank"
                rel="noreferrer"
              >
                API reference
              </a>
              <button type="button" className="btn btn-primary" onClick={() => setOpen(false)}>
                Done
              </button>
            </>
          }
        >
          <div className="pill-tabs" role="tablist" style={{ marginBottom: 0 }}>
            {snippets.map((s, i) => (
              <button
                key={s.label}
                type="button"
                role="tab"
                aria-selected={tab === i}
                onClick={() => setTab(i)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <CodeBlock code={snippets[tab]?.code ?? ''} />
        </Dialog>
      ) : null}
    </>
  );
}

/* ---------- Export ---------- */

/** Download rows as a CSV file. */
export function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0] as object);
  const cell = (v: unknown) => {
    const s = Array.isArray(v) ? v.join('; ') : v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => cell(r[h])).join(',')),
  ].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- Toast ---------- */

export function useToast() {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 2500);
    return () => clearTimeout(t);
  }, [message]);
  const node = message ? (
    <div className="toast" role="status">
      {message}
    </div>
  ) : null;
  return { show: setMessage, node };
}

/** Value that only updates after `ms` without changes (for search boxes). */
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
